/**
 * Jerarquía de Excepciones para API
 *
 * Implementa el patrón de excepciones tipadas según Clean Code y SOLID:
 * - Fail Fast: Detecta errores inmediatamente
 * - Excepciones sobre códigos de retorno
 * - Jerarquía clara para manejo específico
 *
 * @see Clean Code - Chapter 7: Error Handling
 * @see Effective Java - Item 69: Use exceptions for exceptional conditions
 */

// ============================================================================
// BASE ERROR
// ============================================================================

/**
 * Error base para todas las excepciones de API
 */
export class ApiError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
    public readonly originalError?: any
  ) {
    super(message);
    this.name = 'ApiError';

    // Mantiene el stack trace correcto en V8
    if (typeof (Error as any).captureStackTrace === 'function') {
      (Error as any).captureStackTrace(this, this.constructor);
    }
  }
}

// ============================================================================
// CLIENT ERRORS (4xx)
// ============================================================================

/**
 * Error 400 - Bad Request / Validación de Negocio
 * Usado cuando el servidor rechaza la petición por validaciones de negocio
 */
export class ValidationError extends ApiError {
  constructor(
    message: string,
    public readonly code?: string | number,
    public readonly fields?: Record<string, string[]>,
    originalError?: any
  ) {
    super(400, message, originalError);
    this.name = 'ValidationError';
  }
}

/**
 * Error 401 - Unauthorized
 * Usuario no autenticado
 */
export class UnauthorizedError extends ApiError {
  constructor(message: string = 'No autenticado', originalError?: any) {
    super(401, message, originalError);
    this.name = 'UnauthorizedError';
  }
}

/**
 * Error 403 - Forbidden
 * Usuario autenticado pero sin permisos
 */
export class ForbiddenError extends ApiError {
  constructor(message: string = 'Sin permisos para esta acción', originalError?: any) {
    super(403, message, originalError);
    this.name = 'ForbiddenError';
  }
}

/**
 * Error 404 - Not Found
 * Recurso no encontrado
 */
export class NotFoundError extends ApiError {
  constructor(message: string = 'Recurso no encontrado', originalError?: any) {
    super(404, message, originalError);
    this.name = 'NotFoundError';
  }
}

/**
 * Error 409 - Conflict
 * Conflicto con el estado actual (ej: duplicados)
 */
export class ConflictError extends ApiError {
  constructor(message: string = 'Conflicto con el recurso existente', originalError?: any) {
    super(409, message, originalError);
    this.name = 'ConflictError';
  }
}

/**
 * Error 422 - Unprocessable Entity
 * La sintaxis es correcta pero los valores son inválidos
 */
export class UnprocessableEntityError extends ApiError {
  constructor(
    message: string = 'Datos inválidos',
    public readonly errors?: Record<string, string[]>,
    originalError?: any
  ) {
    super(422, message, originalError);
    this.name = 'UnprocessableEntityError';
  }
}

/**
 * Error 429 - Too Many Requests
 * Rate limit excedido
 */
export class RateLimitError extends ApiError {
  constructor(
    message: string = 'Demasiadas peticiones, intenta más tarde',
    public readonly retryAfter?: number,
    originalError?: any
  ) {
    super(429, message, originalError);
    this.name = 'RateLimitError';
  }
}

// ============================================================================
// SERVER ERRORS (5xx)
// ============================================================================

/**
 * Error 500 - Internal Server Error
 * Error general del servidor
 */
export class ServerError extends ApiError {
  constructor(message: string = 'Error interno del servidor', originalError?: any) {
    super(500, message, originalError);
    this.name = 'ServerError';
  }
}

/**
 * Error 502 - Bad Gateway
 * Error de gateway/proxy
 */
export class BadGatewayError extends ApiError {
  constructor(message: string = 'Error de gateway', originalError?: any) {
    super(502, message, originalError);
    this.name = 'BadGatewayError';
  }
}

/**
 * Error 503 - Service Unavailable
 * Servicio temporalmente no disponible
 */
export class ServiceUnavailableError extends ApiError {
  constructor(message: string = 'Servicio no disponible', originalError?: any) {
    super(503, message, originalError);
    this.name = 'ServiceUnavailableError';
  }
}

/**
 * Error 504 - Gateway Timeout
 * Timeout del gateway
 */
export class GatewayTimeoutError extends ApiError {
  constructor(message: string = 'Tiempo de espera agotado', originalError?: any) {
    super(504, message, originalError);
    this.name = 'GatewayTimeoutError';
  }
}

// ============================================================================
// NETWORK ERRORS
// ============================================================================

/**
 * Error de red (sin conexión, DNS, etc.)
 */
export class NetworkError extends ApiError {
  constructor(message: string = 'Error de conexión', originalError?: any) {
    super(0, message, originalError);
    this.name = 'NetworkError';
  }
}

/**
 * Error de timeout
 */
export class TimeoutError extends ApiError {
  constructor(message: string = 'La petición tardó demasiado', originalError?: any) {
    super(0, message, originalError);
    this.name = 'TimeoutError';
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Crea la excepción apropiada según el código de status HTTP
 */
export function createApiError(
  statusCode: number,
  message?: string,
  data?: any,
  originalError?: any
): ApiError {
  // Client Errors (4xx)
  switch (statusCode) {
    case 400:
      return new ValidationError(
        message || data?.message || 'Validación fallida',
        data?.code,
        data?.fields,
        originalError
      );
    case 401:
      return new UnauthorizedError(message || data?.message, originalError);
    case 403:
      return new ForbiddenError(message || data?.message, originalError);
    case 404:
      return new NotFoundError(message || data?.message, originalError);
    case 409:
      return new ConflictError(message || data?.message, originalError);
    case 422:
      return new UnprocessableEntityError(
        message || data?.message,
        data?.errors,
        originalError
      );
    case 429:
      return new RateLimitError(
        message || data?.message,
        data?.retryAfter,
        originalError
      );
  }

  // Server Errors (5xx)
  if (statusCode >= 500) {
    switch (statusCode) {
      case 502:
        return new BadGatewayError(message || data?.message, originalError);
      case 503:
        return new ServiceUnavailableError(message || data?.message, originalError);
      case 504:
        return new GatewayTimeoutError(message || data?.message, originalError);
      default:
        return new ServerError(message || data?.message, originalError);
    }
  }

  // Fallback para otros códigos
  return new ApiError(
    statusCode,
    message || data?.message || `Error HTTP ${statusCode}`,
    originalError
  );
}

/**
 * Type guard para verificar si un error es de tipo ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Type guard para verificar si un error es de validación
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Type guard para verificar si un error es del servidor
 */
export function isServerError(error: unknown): error is ServerError {
  return error instanceof ServerError;
}

/**
 * Type guard para verificar si un error es de red
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Extrae un mensaje amigable para el usuario de cualquier error
 */
export function getUserFriendlyMessage(error: unknown): string {
  if (isApiError(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Ha ocurrido un error inesperado';
}
